import { useCallback, useMemo, useState } from "react";

import type { BundleShiftPay, OpenBundleShift, ShiftTimeData } from "../types";
import { wouldProposedShiftCauseOverworking } from "../wouldProposedShiftCauseOverworking";

interface UseShiftBundleSelectionProps {
  bundleShifts: OpenBundleShift[];
  bookedShifts: ShiftTimeData[];
  requiredShiftCount: number;
  bundleShiftPays?: Map<string, BundleShiftPay>;
}

interface DisabledShiftInfo {
  reason: string;
}

export function useShiftBundleSelection(props: UseShiftBundleSelectionProps) {
  const { bundleShifts, bookedShifts, requiredShiftCount, bundleShiftPays } = props;

  const [selectedShiftIds, setSelectedShiftIds] = useState<Set<string>>(() => new Set());

  const selectedShifts = useMemo(
    () => bundleShifts.filter((shift) => selectedShiftIds.has(shift.id)),
    [bundleShifts, selectedShiftIds]
  );

  const disabledShiftMap = useMemo(() => {
    const selectedShiftTimeData: ShiftTimeData[] = selectedShifts.map((shift) => ({
      start: shift.attributes.start,
      end: shift.attributes.end,
    }));

    const existingShifts: ShiftTimeData[] = [...bookedShifts, ...selectedShiftTimeData];

    const disabled = new Map<string, DisabledShiftInfo>();

    for (const shift of bundleShifts) {
      if (selectedShiftIds.has(shift.id)) {
        continue;
      }

      const shiftTimeData = { start: shift.attributes.start, end: shift.attributes.end };

      if (wouldProposedShiftCauseOverworking(shiftTimeData, existingShifts)) {
        disabled.set(shift.id, { reason: "Over hour limit" });
      }
    }

    return disabled;
  }, [bundleShifts, bookedShifts, selectedShifts, selectedShiftIds]);

  const toggleShift = useCallback((shiftId: string) => {
    setSelectedShiftIds((previousSelectedShiftIds) => {
      const nextSelectedShiftIds = new Set(previousSelectedShiftIds);
      if (nextSelectedShiftIds.has(shiftId)) {
        nextSelectedShiftIds.delete(shiftId);
      } else {
        nextSelectedShiftIds.add(shiftId);
      }

      return nextSelectedShiftIds;
    });
  }, []);

  const selectedCount = selectedShiftIds.size;

  const totalSelectedPayInMinorUnits = useMemo(() => {
    if (!bundleShiftPays) {
      return undefined;
    }

    let total = 0;
    for (const shiftId of selectedShiftIds) {
      const shiftPay = bundleShiftPays.get(shiftId);
      if (shiftPay !== undefined) {
        total += shiftPay.totalPayInMinorUnits;
      }
    }

    return total;
  }, [selectedShiftIds, bundleShiftPays]);

  const allUnselectedAreDisabled = useMemo(() => {
    const unselectedShifts = bundleShifts.filter((shift) => !selectedShiftIds.has(shift.id));
    return (
      unselectedShifts.length > 0 &&
      unselectedShifts.every((shift) => disabledShiftMap.has(shift.id))
    );
  }, [bundleShifts, selectedShiftIds, disabledShiftMap]);

  const isBookingAllowed =
    selectedCount >= requiredShiftCount || (selectedCount > 0 && allUnselectedAreDisabled);

  return {
    selectedShiftIds,
    selectedCount,
    disabledShiftMap,
    toggleShift,
    totalSelectedPayInMinorUnits,
    isBookingAllowed,
  };
}
